import { useConfigStore } from '@/stores';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useOrientation, useTickSoundInteraction } from '@/hooks';
import type { PoiContentSlide } from '@/types/shop';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { memo, useCallback, useMemo } from 'react';
import { AppUtils } from '@/utils/app';
import { POI_FIELD_KEY } from '@/enums/poi';

interface ShopCarouselItemProps {
    content: PoiContentSlide;
    tabIndex: number;
    id?: string;
    onClick?: (data: PoiContentSlide) => void;
}

const ShopCarouselItem = memo(({ content, tabIndex, id, onClick }: ShopCarouselItemProps) => {
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const { t, i18n } = useTranslation();
    const { isHorizontal } = useOrientation();
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();

    const ariaLabel = useMemo(() => {
        const parts: string[] = [content.name];
        parts.push(t('shop.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(content.floor) }));

        if (buildings?.length && buildings.length > 1 && content.buildingName) {
            parts.push(content.buildingName);
        }
        return parts.join(', ');
    }, [buildings?.length, content.buildingName, content.floor, content.name, t]);

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const parent = e.currentTarget;
            parent.classList.add('poi-active');
            handleInteraction(e, () => onClick?.(content));
        },
        [handleInteraction, onClick, content]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => handleKeyboardInteraction(e, () => onClick?.(content)),
        [handleKeyboardInteraction, onClick, content]
    );

    const isTaxRefund = useMemo(() => {
        const taxFreeItem = content.data?.managedContents?.find((item) => item.field_key === POI_FIELD_KEY.TAX_FREE_YN);
        if (!taxFreeItem) return false;
        const lang = i18n.language as keyof typeof taxFreeItem.content;
        return taxFreeItem.content?.[lang] === 'Y' || taxFreeItem.content?.['ko'] === 'Y';
    }, [content.data?.managedContents, i18n.language]);

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            key={content.id}
            id={id}
            tabIndex={tabIndex}
            data-interactive
            data-playsound="ting"
            data-role={t('common.button.tts.popup_button')}
            className={clsx(styles.wrapper, {
                [styles['wrapper__contrast']]: contrast,
                [styles['wrapper__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                [styles['wrapper__vertical']]: !isHorizontal,
            })}
            aria-label={ariaLabel}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <div
                className={clsx(styles['wrapper__poi--image'], {
                    [styles['wrapper__poi--image__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                })}
            >
                {content.url ? <img src={content.url} alt={`${content.name} logo`} loading="lazy" data-role={t('common.data_role.image')} /> : <PlaceHolder />}
            </div>

            <div
                className={clsx(styles['wrapper__poi--info'], {
                    [styles['wrapper__poi--info__contrast']]: contrast,
                    [styles['wrapper__poi--info__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                })}
            >
                <div className="flex flex-col">
                    <p className={clsx(styles['wrapper__poi--info--meta'])}>
                        <span>{content.floor}</span>
                        {buildings?.length && buildings.length > 1 && (
                            <>
                                ·<span>{content.buildingName}</span>
                            </>
                        )}
                    </p>
                    <h3 className={clsx(styles['wrapper__poi--info--title'])}>
                        {content.name?.length > 40 ? `${content.name.slice(0, 40)}...` : content.name}
                    </h3>
                </div>
                {isTaxRefund && <div className={clsx(styles['wrapper__poi--info--refund'])}>{t('shop.tax_refund')}</div>}
            </div>
        </div>
    );
});

ShopCarouselItem.displayName = 'ShopCarouselItem';

export default ShopCarouselItem;
